import axios, { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import License from "../common/models/License";
import Listing from "../common/models/Listing";
import MasterCopy, { SongDetailResponse } from "../common/models/MasterCopy";
import SongCollection, {
  SongCollectionResponse,
} from "../common/models/SongCollections";
import { User, UserDetailResponse } from "../common/models/User";
import {
  getRemainingDays,
  getRemainingHours,
  getRemainingListingTime,
  getRemainingMinutes,
} from "../common/utils/Time";
import ImageButton from "../components/Buttons/ImageButton";
import SimpleButton from "../components/Buttons/SimpleButton";
import MasterCopyCard from "../components/Card/SongCard";
import Column from "../components/Column/Column";
import HalfColumn from "../components/Column/HalfColumn";
import Footer from "../components/Footer/Footer";
import HowItWorks, { HowItWorksRow } from "../components/howitworks/HowItWorks";
import RoundedProfilePic from "../components/Images/RoundedProfilePic";
import { Modal } from "../components/Modal/Modal";
import { NavBar } from "../components/NavBar";
import FullHeightRow from "../components/Row/FullHeightRow";
import Row from "../components/Row/Row";
import { SectionTitle } from "../components/Text/SectionTitle";
import Text from "../components/Text/Text";
import { useAuth } from "../hooks/useAuth";
import success_icon from "../imgs/checkout_success_icon.svg";
import license_icon from "../imgs/license.svg";
import arrow_right from "./imgs/arrow_right.svg";
import collectible_nft_icon from "./imgs/collectible_nft_icon.svg";
import empty_collector_icon from "./imgs/empty_collector_icon.svg";
import pause_icon from "./imgs/pause.png";
import play_icon from "./imgs/play.png";
import ListToMarketplaceModal from "./modals/ListToMarketplaceModal";
import Song, { SongType } from "./models/Song";
import SongCheckoutModal from "./SongCheckoutModal";
import SongLicenseModal from "./SongLicenseModal";

import { ethers } from "ethers";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import ComposeNFTArtifact from "../artifacts/contracts/ComposeNFT.sol/ComposeNFT.json";
import LicenseSoulboundNFTArtifact from "../artifacts/contracts/LicenseSoulboundNFT.sol/LicenseSoulboundNFT.json";
import SongToken, { SongTokenResponse } from "../common/models/SongToken";
import Dropdown, { DropdownText } from "../components/Dropdown/Dropdown";
import DropdownContent from "../components/Dropdown/DropdownContent";
import DropdownItem from "../components/Dropdown/DropdownItem";
import SearchInput from "../components/Inputs/SearchInput";
import { NotificationMessage } from "../components/Notification/NotificationMessage";
import ScrollContainer from "../components/Scroll/ScrollContainer";
import eth_icon from "../imgs/eth_icon_blue.svg";
import default_user_pic from "../imgs/user_icon.svg";
import { getCutWalletAddress, sleep } from "../utils";
import arrow_left from "./imgs/arrow-left.svg";
import copy_logo from "./imgs/copy.svg";
import left_control from "./imgs/left_control.svg";
import master_copy_icon from "./imgs/master_copy_icon.svg";
import right from "./imgs/right.svg";
import right_control from "./imgs/right_control.svg";
import share from "./imgs/share.svg";
import spotify_logo from "./imgs/spotify_logo.svg";
import twitter_logo from "./imgs/twitter.svg";
import youtube_logo from "./imgs/youtube_logo.svg";
import ListTokenToMarketplaceModal from "./modals/ListTokenToMarketplaceModal";
import ListToSecondaryMarketModal from "./modals/ListToSecondaryMarketModal";

interface SongDetailProps {
  theme: string;
  toggleTheme: any;
  isCollection: boolean;
}
const PURCHASE_SUCCESS_MSG = "Congratulations on your new purchase!";
const LIST_TO_MARKETPLACE_SUCCESS_MSG =
  "Congratulations on listing your NFT to the marketplace!";

const Image = styled("div")<{ src: string }>`
  background: url(${(props) => props.src}) center no-repeat;
  background-size: cover;
  background-clip: border-box;
  width: 100%;
  min-height: 80vh;

  @media screen and (max-width: 650px) {
    min-height: 50vh;
  }
`;

const PlayOverlay = styled("div")`
  position: absolute;
  bottom: 3%;
  left: 10%;
  width: 70%;
  background: rgba(101, 101, 101, 0.15);
  border: 1px solid #ffffff;
  backdrop-filter: blur(500px);
  /* Note: backdrop-filter has minimal browser support */

  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-inline: 3%;
  padding-top: 1%;
`;
const SongText = styled(Text)`
  padding-top: 5%;
  font-size: 36px;
  margin: 5px;
  font-weight: 600;

  @media screen and (max-width: 1600px) {
    font-size: 24px;
  }
`;

const ArtistText = styled(Text)`
  font-size: 20px;
  margin: 5px;
`;
const SeekBar = styled.input.attrs({ type: "range" })`
  background: linear-gradient(
    to right,
    #fff 0%,
    #fff 0%,
    #c6c6c6 0%,
    #c6c6c6 100%
  );

  border-radius: 8px;
  height: 8px;
  width: 100%;
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    width: 20px;
    -webkit-appearance: none;
    height: 20px;
    background: #fff;
    border-radius: 50%;
  }
`;

const GeneralButton = styled(SimpleButton)`
  white-space: nowrap;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const ManageLicensesButton = styled(GeneralButton)`
  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const BuyItOnDesktop = styled(Text)`
  display: none;
  @media screen and (max-width: 650px) {
    display: block;
    margin-left: auto;
    margin-right: 10px;
  }
`;

const LicenseButton = styled(GeneralButton)`
  margin-top: 13%;
`;

const DurationRow = styled(Row)`
  justify-content: space-between;
  width: 100%;
`;
const SeekBarRow = styled(Row)`
  width: 100%;
`;

const ButtonsRow = styled(Row)`
  margin-top: auto;
  justify-content: space-between;
  padding-bottom: 5%;
  padding-right: 5%;
`;

const LaunchCountdown = styled(Column)`
  margin-top: auto;
  justify-content: space-between;
  padding-bottom: 5%;
  padding-right: 5%;

  @media screen and (max-width: 650px) {
    margin-top: 5%;
  }
`;

const LaunchCountdownText = styled(Text)`
  @media screen and (max-width: 650px) {
    text-align: center;
    font-size: 24px;
  }
`;

const LaunchCountdownTimeText = styled(Text)`
  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const PriceContainer = styled(Column)`
  justify-content: flex-start;
  min-width: 30%;
  max-width: 60%;
  margin-top: auto;
  padding-top: 1%;
  padding-bottom: 1%;
  padding-left: 2%;
  background: rgba(196, 196, 196, 0.1);
  border: 1px solid #ffffff;
  backdrop-filter: blur(19.6px);

  border-radius: 10px;

  @media screen and (max-width: 650px) {
    width: 90%;
  }
`;
const BuyersPriceContainer = styled(PriceContainer)`
  width: 30%;
  height: 60%;
  @media screen and (max-width: 650px) {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
  }

  @media screen and (min-width: 651px) and (max-width: 1700px) {
    width: 40%;
    height: 70%;
  }
`;

const LicensePriceContainer = styled(PriceContainer)`
  height: 60%;
  min-width: 0;
  padding-right: 3%;
  @media screen and (max-width: 650px) {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
  }

  @media screen and (min-width: 651px) and (max-width: 1700px) {
    height: 70%;
  }
`;

const LicenseContainer = styled(PriceContainer)`
  width: 40%;
  height: 60%;
  flex-direction: column;
  justify-content: center;

  @media screen and (min-width: 651px) and (max-width: 1700px) {
    width: 40%;
    height: 70%;
  }
`;

const RightColumn = styled(HalfColumn)`
  position: relative;
  justify-content: flex-start;
  padding-left: 3%;
  min-height: 80vh;
  max-height: 80vh;
  background: linear-gradient(180deg, #000000 0%, rgba(25, 0, 81, 0) 100%);

  @media screen and (max-width: 650px) {
    width: 100%;
    height: 70%;
  }
`;

const PriceText = styled("p")`
  color: ${(props) => (props.theme.themeType === "dark" ? "white" : "#9D7CFF")};
  font-weight: 600;
  font-size: 22px;
`;

const ArtistProfileSection = styled(Row)`
  justify-content: space-between;
`;

const ClickableContainer = styled(Row)`
  &:hover {
    cursor: pointer;
  }
`;

const SongAvailableText = styled(Text)`
  text-decoration: underline;
  display: block;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-left: 5px;

  &:hover {
    cursor: pointer;
  }
`;

interface CheckoutSuccessModalProps {
  show: boolean;
  showModalToggle: any;
  theme: string;
  successMessage: string;
}

const CheckoutSuccessModalContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #000d30;
  min-width: 30%;
  padding-inline: 5%;
  padding-block: 3%;
  border-radius: 20px;
`;

const CheckoutSuccessModal = React.forwardRef<
  HTMLDivElement,
  CheckoutSuccessModalProps
>(({ theme, show, showModalToggle, successMessage }, ref) => {
  const onCloseClick = () => {
    showModalToggle(false);
  };

  return (
    <Modal ref={ref} show={show}>
      <CheckoutSuccessModalContainer>
        <Text>{successMessage}</Text>
        <img src={success_icon} />
        <SimpleButton
          style={{ padding: "0.75rem 3rem" }}
          onClick={onCloseClick}
        >
          OK
        </SimpleButton>
      </CheckoutSuccessModalContainer>
    </Modal>
  );
});

const YouMayAlsoLikeSection = styled.div`
  margin-top: auto;
  padding-inline: 10%;
  background: #000d30;
`;

const HowItWorksSection = styled.div`
  padding-inline: 10%;
  padding-top: 2%;
  padding-bottom: 5%;
  background: #000d30;
`;

const TopLeftLicensedRibbon = styled.div`
  color: white;
  height: 35px;
  width: 180px;
  background-color: #424094;
  position: absolute;
  top: 125px;
  left: -6px;
  z-index: 3;
  text-align: center;
  color: #fff;
  font-weight: 500;
  font-size: 24px;
  white-space: nowrap;
  -webkit-transform: rotate(-35deg);
  -moz-transform: rotate(-35deg);
  -ms-transform: rotate(-35deg);
  -o-transform: rotate(-35deg);
  transform: rotate(-35deg);

  &:before {
    box-sizing: border-box;
    border-style: solid;
    border-color: transparent;
    border-width: 40px;
    border-left-width: 30px;
    border-right-width: 55px;
    content: "";
    display: block;
    left: -30px;
    position: absolute;
    width: 145%;
    border-top-width: 0px;
    border-bottom-color: #424094;
    top: -5px;
    z-index: -1;
  }
`;

const TopLeftRevokedRibbon = styled.div`
  color: white;
  height: 35px;
  width: 180px;
  background-color: black;
  position: absolute;
  top: 10%;
  left: -6px;
  z-index: 3;
  text-align: center;
  color: #fff;
  font-weight: 500;
  font-size: 24px;
  white-space: nowrap;
  -webkit-transform: rotate(-35deg);
  -moz-transform: rotate(-35deg);
  -ms-transform: rotate(-35deg);
  -o-transform: rotate(-35deg);
  transform: rotate(-35deg);

  &:before {
    box-sizing: border-box;
    border-style: solid;
    border-color: transparent;
    border-width: 40px;
    border-left-width: 30px;
    border-right-width: 55px;
    content: "";
    display: block;
    left: -30px;
    position: absolute;
    width: 145%;
    border-top-width: 0px;
    border-bottom-color: black;
    top: -5px;
    z-index: -1;
  }
`;

const SongDetailPageContainer = styled(FullHeightRow)`
  background: #000d30;
  align-items: flex-start;
  padding-top: 3%;
  @media screen and (max-width: 650px) {
    flex-wrap: wrap;
    padding-top: 17%;
  }
  @media screen and (min-width: 651px) and (max-width: 1079px) {
    padding-top: 8%;
  }
  @media screen and (min-width: 1080px) and (max-width: 1700px) {
    padding-top: 6%;
  }
`;

const DetailsSection = styled.div`
  padding-block: 5%;
  background: #000d30;
  display: flex;
  flex-direction: column;
  gap: 30px;
  flex: 1;

  @media screen and (max-width: 650px) {
    width: 100%;
    flex: auto;
  }
`;

const DetailsInfo = styled("div")<{ active?: boolean }>`
  display: flex;
  justify-content: flex-start;
  padding-inline: 1%;
  background: rgba(196, 196, 196, 0.1);
  border: 0.2px solid #ffffff40;
  backdrop-filter: blur(19.6px);
  border-radius: 10px;

  ${(props) =>
    props.active &&
    css`
      &:hover {
        cursor: pointer;
      }

      &:active {
        transform: scale(0.98);
        box-shadow: 3px 2px 22px 1px rgba(0, 0, 0, 0.24);
      }
    `}

  @media screen and (max-width: 650px) {
    width: 100%;
  }
`;

const InfoSection = styled.div`
  display: flex;
  background: #000d30;
  padding-inline: 10%;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 5%;
`;

const PlayButton = styled(ImageButton)`
  width: 75px;
  height: 75px;
  margin-left: auto;
  z-index: 10;

  @media screen and (max-width: 650px) {
    width: 50px;
    height: 50px;
  }
`;

const AlbumCoverColumn = styled(HalfColumn)`
  position: relative;

  @media screen and (max-width: 650px) {
    width: 100%;
  }
`;

const ModalOutlet = styled(Outlet)`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const NumberInput = styled("input")`
  margin: 5px;
  margin-top: 20px;
  margin-bottom: 20px;
  font-family: "Spartan";
  font-style: normal;
  font-weight: 500;
  font-size: 48px;
  line-height: 54px;
  border: none;

  background: white;
  &:focus {
    color: white;
    outline: none;
  }

  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const NumberContainer = styled("div")`
  width: 50px;
  margin-right: 15px;
  filter: drop-shadow(2px 16px 19px rgba(0, 0, 0, 0.09));
  backdrop-filter: blur(40px);
  /* Note: backdrop-filter has minimal browser support */

  border-radius: 8px;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const LaunchTimeText = styled.div`
  display: none;

  @media screen and (max-width: 650px) {
    display: block;
    text-align: center;
  }
`;

const ExtraInfoContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0.5px solid #ffffff90;
  border-radius: 5px;
  padding-left: 5%;
  margin-top: 5%;

  @media screen and (max-width: 650px) {
    margin-bottom: 10px;
    margin-right: 0;
  }
`;

const GradientText = styled.p`
  background: linear-gradient(180deg, #9d7cff 0%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  font-style: normal;
  margin-block: 0;
  margin-inline: 0;
  font-weight: 600;
  font-size: 18px;
  margin-right: 10px;
`;

const CircularProgressBarWrapper = styled(CircularProgressbar)`
  margin-top: 5%;
`;

interface CollectiblePriceContainerProps {
  song: Song;
  collectorsRef: any;
  onListToSecondaryMarketplaceClick: any;
}

const CollectiblePriceContainer: React.FC<CollectiblePriceContainerProps> = ({
  song,
  collectorsRef,
  onListToSecondaryMarketplaceClick,
}) => {
  return (
    <PriceContainer>
      <Row style={{ justifyContent: "flex-start", gap: "10px" }}>
        <img style={{ width: "30px" }} src={collectible_nft_icon} />
        <PriceText>Collectible NFT</PriceText>
      </Row>
      {song.quantity - song.minted > 0 ? (
        <>
          <Text
            style={{
              marginBlock: 0,
              color: "#ffffff50",
              fontSize: "14px",
            }}
          >
            Listed Price {song.price} ETH
          </Text>
          <Text style={{ marginBlock: 0 }}>
            {song.quantity - song.minted} Unique NFTs Remaining
          </Text>
          <Row gap={"1rem"} style={{ justifyContent: "flex-start" }}>
            <ExtraInfoContainer>
              <GradientText>{song.minted * song.price} ETH Raised</GradientText>
              <SimpleButton
                onClick={() => {
                  collectorsRef.current.scrollIntoView();
                }}
              >
                View Fans
              </SimpleButton>
            </ExtraInfoContainer>
          </Row>
        </>
      ) : (
        <>
          <Text style={{ marginBlock: 0 }}>
            Total Raised: {song.price * song.minted} ETH
          </Text>
          <Row gap={"1rem"} style={{ justifyContent: "flex-start" }}>
            <ExtraInfoContainer>
              <GradientText>Sold Out</GradientText>
              <SimpleButton onClick={onListToSecondaryMarketplaceClick}>
                List to Marketplace
              </SimpleButton>
            </ExtraInfoContainer>
          </Row>
        </>
      )}
    </PriceContainer>
  );
};

interface SecondarySalesBoxProps {
  currentTokenSort: string;
  setCurrentTokenSort: any;
  songTokensForSale: SongToken[];
  song: Song;
  songTokenClick: any;
}
const TokenForSale = styled.div<{ disabled: boolean }>`
  border: 0.4px solid #ffffff90;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  flex: 0.25;
  text-align: center;

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        cursor: pointer;
      }
    `};

  @media screen and (max-width: 650px) {
    flex: 0 0 40%;
    margin-top: 10%;
    margin-right: 0;
  }
`;

const TokenIdContainer = styled.div`
  background: rgba(193, 193, 193, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 20px;
  border-radius: 5px;
`;

const TokenIdText = styled(Text)`
  font-size: 32px;

  @media screen and (max-width: 1440px) {
    font-size: 24px;
  }
`;

const TokenRow = styled(Row)`
  justify-content: flex-start;
  gap: 7%;
  flex-basis: 25%;
  flex-wrap: wrap;

  @media screen and (max-width: 650px) {
    gap: 10%;
  }
  @media screen and (min-width: 651px) and (max-width: 1600px) {
    gap: 3%;
  }
`;

const SecondarySaleBox: React.FC<SecondarySalesBoxProps> = ({
  currentTokenSort,
  setCurrentTokenSort,
  songTokensForSale,
  song,
  songTokenClick,
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [songTokens, setSongTokens] = useState(songTokensForSale);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const ids = songTokensForSale.map((song) => {
      return song.active_listing_id;
    });
    axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "/get_listings",
        JSON.stringify({ ids: ids }),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        setListings(Listing.fromResponse(response));
      });
  }, []);

  const hasToken = () => {
    return (
      songTokens.filter((song) => {
        return auth.user && song.owner === auth.user.wallet_address;
      }).length !== 0
    );
  };

  const isTokenOrCollectibleOwner = () => {
    return (
      hasToken() ||
      (auth.user && song.artist_wallet_address === auth.user.wallet_address)
    );
  };

  const onTokenClick = (songToken: SongToken) => {
    if (!auth.user) {
      navigate("/connect_wallet");
    } else {
      songTokenClick(
        songToken,
        listings.filter((listing) => {
          return listing.id === songToken.active_listing_id;
        })[0]
      );
    }
  };

  const search = (songTokens: Array<SongToken>) => {
    return songTokens.filter((songToken) =>
      songToken.token_id
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase().trim())
    );
  };

  return (
    <SecondarySaleContainer>
      <Text bold={true} size={"24px"}>
        Collectible Resale
      </Text>
      <Row align={"flex-start"}>
        <Dropdown>
          <TokenSortContainer>
            {currentTokenSort}
            <img src={right} style={{ marginBottom: "-2%" }} />
          </TokenSortContainer>
          <DropdownContent
            width={"100%"}
            minWidth={"140px"}
            transform={"translate(0%,5%)"}
            bg={"rgba(196, 196, 196, 0.1)"}
            blur={true}
          >
            <DropdownItem
              onClick={() => {
                setCurrentTokenSort("Floor Price");
                songTokens.sort((a, b) => {
                  return a.price < b.price ? -1 : 1;
                });
                setSongTokens([...songTokens]);
              }}
            >
              <DropdownText>Floor Price</DropdownText>
            </DropdownItem>

            <DropdownItem
              onClick={() => {
                setCurrentTokenSort("High Price");
                songTokens.sort((a, b) => {
                  return a.price > b.price ? -1 : 1;
                });
                setSongTokens([...songTokens]);
              }}
            >
              <DropdownText>High Price</DropdownText>
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                setCurrentTokenSort("Ascending");
                songTokens.sort((a, b) => {
                  return a.token_id < b.token_id ? -1 : 1;
                });
                setSongTokens([...songTokens]);
              }}
            >
              <DropdownText>Ascending</DropdownText>
            </DropdownItem>

            <DropdownItem
              onClick={() => {
                setCurrentTokenSort("Descending");
                songTokens.sort((a, b) => {
                  return a.token_id > b.token_id ? -1 : 1;
                });
                setSongTokens([...songTokens]);
              }}
            >
              <DropdownText>Descending</DropdownText>
            </DropdownItem>
          </DropdownContent>
        </Dropdown>

        <div style={{ width: "40%", marginLeft: "auto" }}>
          <SearchInput
            onTextChange={(text: string) => {
              setSearchQuery(text);
            }}
            placeholder={"Search by token #"}
            theme={"dark"}
          />
        </div>
      </Row>
      <Row align="flex-start" style={{ marginBlock: "10px" }}>
        <Text>{songTokensForSale.length} Available</Text>
        <div style={{ marginLeft: "auto" }}>
          <ImageButton
            bg={left_control}
            onClick={() => {
              setPage(page === 0 ? 0 : page - 1);
            }}
          />
          &nbsp; &nbsp; &nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;
          <ImageButton
            bg={right_control}
            onClick={() => {
              setPage(
                page === Math.floor(songTokensForSale.length / 4)
                  ? Math.floor(songTokensForSale.length / 4)
                  : page + 1
              );
            }}
          />
        </div>
      </Row>

      <TokenRow>
        {search(songTokens).length > 0 ? (
          search(songTokens)
            .map((songToken, index) => (
              <TokenForSale
                key={"token_for_sale_" + index}
                onClick={() => {
                  if (!isTokenOrCollectibleOwner()) {
                    onTokenClick(songToken);
                  }
                }}
                disabled={isTokenOrCollectibleOwner()}
              >
                <TokenIdContainer>
                  <div>
                    #<TokenIdText bold={true}>{songToken.token_id}</TokenIdText>
                  </div>
                </TokenIdContainer>
                {getCutWalletAddress(songToken.owner)}
                <Row style={{ marginTop: "10px", marginBottom: "10px" }}>
                  <img src={eth_icon} />
                  {songToken.price} ETH
                </Row>
              </TokenForSale>
            ))
            .slice(page * 4, page * 4 + 4)
        ) : (
          <Text>Sorry, we couldn't find any results.</Text>
        )}
      </TokenRow>
    </SecondarySaleContainer>
  );
};

const RoyaltyPercentage = ({ song }) => {
  return (
    <Column>
      <Row style={{ alignItems: "flex-start" }}>
        <GradientText style={{ fontSize: "48px", marginRight: "0" }}>
          {song.royalty_percentage}
        </GradientText>
        <Text size={"24px"}>%</Text>
      </Row>
      <Text style={{ margin: 0 }}>Royalty Percentage</Text>
    </Column>
  );
};

const SecondarySaleContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  padding-bottom: 30px;
  border: 1px solid white;
  border-radius: 10px;
  margin-right: 10%;

  @media screen and (max-width: 650px) {
    margin-right: 10px;
  }
`;

const TokenSortContainer = styled.label`
  background: rgba(193, 193, 193, 0.2);
  border-radius: 8px;
  padding: 10px;
  &:hover {
    cursor: pointer;
  }

  @media screen and (max-width: 1440px) {
    font-size: 12px;
  }
`;

const TokenOwnerInfoContainer = styled(Column)`
  background: rgba(196, 196, 196, 0.1);
  border-radius: 12px;
  padding: 10px;
`;

const LicenseBuyItOnDesktopRow = styled(Row)`
  border: 0.5px solid #ffffff90;
  border-radius: 5px;
  margin-bottom: 10px;
  margin-right: 10%;

  @media screen and (min-width: 650px) {
    display: none;
  }
`;

const ClickableRow = styled(Row)`
  margin-left: 10px;
  gap: 5px;
  &:hover {
    cursor: pointer;
  }
`;

const CollectorsScrollContainer = styled(ScrollContainer)<{
  showBottomBorder?: boolean;
}>`
  height: 410px;
  border-bottom: ${(props) =>
    props.showBottomBorder ? "1px solid white" : "none"};

  @media screen and (max-width: 1440px) {
    height: 350px;
  }
`;

const ShareSheet = styled(Column)`
  position: absolute;
  padding-inline: 20%;
  background: rgba(196, 196, 196, 0.1);
  border: 0.3px solid #ffffff;
  backdrop-filter: blur(19.6px);
  border-radius: 10px;
`;

const DescriptionText = styled(Text)`
  margin-top: 5%;
  margin-right: 10%;
  overflow-y: hidden;
  height: 80px;
  font-size: 14px;

  &:hover {
  overflow-y: scroll;
}

  ::-webkit-scrollbar {
    width: 8px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #D0D0D0;
    border-radius: 10px;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #FFFFFFB3;
  }
`;

export const SongDetailPage: React.FC<SongDetailProps> = ({
  theme,
  toggleTheme,
  isCollection,
}) => {
  const song_id = useParams();
  const [song, setSong] = useState<Song>();
  const audioPlayer = useRef<HTMLAudioElement>(null);
  const progressBar = useRef<HTMLInputElement>(null);
  const animationRef = useRef(null);
  const collectorsRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [artist, setArtist] = useState<User>();
  const [songTokens, setSongTokens] = useState<SongToken[]>([]);
  const [currentTokenSort, setCurrentTokenSort] = useState("Floor Price");
  const [songsByArtist, setSongsByArtist] = useState<Array<Song>>();
  const [isOwnerSong, setIsOwnerSong] = useState(false);
  const [showSongCheckoutModal, setShowSongCheckoutModal] = useState(false);
  const [checkoutWithToken, setCheckoutWithToken] = useState(false);
  const [checkoutSongToken, setCheckoutSongToken] = useState<Song>();
  const [showListTokenToMarketplace, setShowListTokenToMarketplace] =
    useState(false);
  const [showCheckoutSuccess, setShowCheckoutSuccessModal] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const [listing, setListing] = useState<Listing>(null);
  const [listingTime, setListingTime] = useState<{
    days: number;
    hrs: number;
    mnts: number;
    seconds: number;
  }>();

  const [showListToMarketplaceModal, setShowListToMarketplaceModal] =
    useState(false);
  const [showListToSecondaryMarketModal, setShowListToSecondaryMarketModal] =
    useState(false);
  const [showListSuccess, setShowListSuccess] = useState(false);
  const [licensed, setLicensed] = useState(false);
  const [showSongLicenseModal, setShowSongLicenseModal] = useState(false);
  const [license, setLicense] = useState<License>();
  const [licenses, setLicenses] = useState<License[]>();

  const [days, setDays] = useState([]);
  const [hours, setHours] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [legalContractURI, setLegalContractURI] = useState("");
  const [licenseLegalContractURI, setLicenseLegalContractURI] = useState("");
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const isPolygonMainnet = process.env.REACT_APP_USE_POLYGON_MAINNET === "true";

  useEffect(() => {
    if (isCollection) {
      getCollection();
    } else {
      getSong();
    }
  }, [song_id, isCollection]);

  useEffect(() => {
    if (listing && parseInt(listing.start_time) > new Date().getTime() / 1000) {
      setDays(getRemainingDays(listing.start_time));
      setHours(getRemainingHours(listing.start_time));
      setMinutes(getRemainingMinutes(listing.start_time));
      const interval = setInterval(() => {
        // Update the date every 30 seconds
        setDays(getRemainingDays(listing.start_time));
        setHours(getRemainingHours(listing.start_time));
        setMinutes(getRemainingMinutes(listing.start_time));
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [listing && listing.start_time]);

  useEffect(() => {
    if (song && song.launch_date > new Date().getTime() / 1000) {
      setDays(getRemainingDays(song.launch_date.toString()));
      setHours(getRemainingHours(song.launch_date.toString()));
      setMinutes(getRemainingMinutes(song.launch_date.toString()));
      const interval = setInterval(() => {
        // Update the date every 30 seconds
        setDays(getRemainingDays(song.launch_date.toString()));
        setHours(getRemainingHours(song.launch_date.toString()));
        setMinutes(getRemainingMinutes(song.launch_date.toString()));
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [song && song.launch_date]);

  useEffect(() => {
    if (
      song &&
      (song.type === SongType.COLLECTIBLE || song.type === SongType.TOKEN)
    ) {
      getTokensFromCollection();
    }
  }, [song && song.type === SongType.COLLECTIBLE]);

  const getTokensFromCollection = () => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_tokens_from_collection?collection_contract_address=" +
          song.nft_address
      )
      .then((response: AxiosResponse) => {
        const songTokens = response.data
          .song_tokens_from_collection as Array<SongToken>;
        setSongTokens(songTokens);
      });
  };

  const getSong = () => {
    axios
      .get<SongDetailResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_details?id=" +
          song_id.songid
      )
      .then((response: AxiosResponse<SongDetailResponse>) => {
        const masterCopy = response.data.masterCopyDetails as MasterCopy;
        setSong(Song.fromMasterCopy(masterCopy));
      });
  };

  const getCollection = () => {
    axios
      .get<SongCollectionResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_collection_details?id=" +
          song_id.songid
      )
      .then((response: AxiosResponse<SongCollectionResponse>) => {
        const songCollection = response.data
          .songCollectionDetails as SongCollection;

        if (!auth.user) {
          setSong(Song.fromSongCollection(songCollection));
          return;
        }
        if (songCollection.artist_wallet_address === auth.user.wallet_address) {
          setSong(Song.fromSongCollection(songCollection));
        } else {
          // Check if current owner owns a song token from this collection
          getSongTokenFromCollection(auth.user, songCollection);
        }
      });
  };

  const getSongTokenFromCollection = (
    user: User,
    songCollection: SongCollection
  ) => {
    axios
      .get<SongTokenResponse>(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_token_from_collection?collection_address=" +
          songCollection.nft_address +
          "&wallet_address=" +
          user.wallet_address
      )
      .then((response: AxiosResponse<SongTokenResponse>) => {
        const songToken = response.data.songToken as SongToken;

        if (songToken !== null && songToken !== undefined) {
          setSong(Song.fromSongToken(songToken, songCollection));
        } else {
          setSong(Song.fromSongCollection(songCollection));
        }
      });
  };

  useEffect(() => {
    if (auth && auth.user && song) {
      if (isCollection) {
        setIsOwnerSong(song.artist_wallet_address === auth.user.wallet_address);
      } else {
        setIsOwnerSong(song.owner_address === auth.user.wallet_address);
      }
      if (song.type === SongType.MASTER_COPY) {
        // Get only if it's a marter copy
        getLicensedSongs(auth.user.wallet_address);
      }
    }
  }, [auth, song]);

  useEffect(() => {
    if (licensed && auth.user && song) {
      getLicenseFromSong();
    }
  }, [licensed, auth.user, song]);

  const getLicenseFromSong = () => {
    // Get license from song

    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_license?wallet_address=" +
          auth.user.wallet_address +
          "&song_id=" +
          song.id
      )
      .then((response: AxiosResponse) => {
        setLicense(response.data.license as License);
      });
  };

  const getLicensedSongs = (wallet_address: string) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_licensed_songs?wallet_address=" +
          wallet_address
      )
      .then((response: AxiosResponse) => {
        const songs = response.data.licensed_songs as Array<MasterCopy>;
        const filteredSongs = songs.filter((licensedSong) => {
          return licensedSong.id === song.id;
        });
        setLicensed(filteredSongs.length > 0);
      });
  };

  useEffect(() => {
    song &&
      axios
        .get(
          process.env.REACT_APP_BACKEND_URL +
            "/get_minted_songs?wallet_address=" +
            song.artist_wallet_address
        )
        .then((response: AxiosResponse) => {
          const mintedSongs = response.data.minted_songs as Array<MasterCopy>;
          getSongsByArtist(mintedSongs);
        });
    song &&
      song.active_listing_id &&
      axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "/get_listings",
          JSON.stringify({ ids: [song.active_listing_id] }),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setListing(Listing.fromResponse(response)[0]);
        });
  }, [song_id, song]);

  const getSongsByArtist = (mintedSongs: Array<MasterCopy>) => {
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_song_collections?wallet_address=" +
          song.artist_wallet_address
      )
      .then((response: AxiosResponse) => {
        const collections = response.data
          .song_collections as Array<SongCollection>;

        const songCollections = collections.map((collection) => {
          return Song.fromSongCollection(collection);
        });
        const minted = mintedSongs.map((masterCopy) => {
          return Song.fromMasterCopy(masterCopy);
        });
        setSongsByArtist(songCollections.concat(minted));
      });
  };

  const calculateTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${returnedMinutes}:${returnedSeconds}`;
  };

  // Get the song artist to display on the modal
  useEffect(() => {
    song &&
      axios
        .get<UserDetailResponse>(
          process.env.REACT_APP_BACKEND_URL +
            "/get_user_details?wallet_address=" +
            song.artist_wallet_address
        )
        .then((response: AxiosResponse<UserDetailResponse>) => {
          const user = response.data.userDetails as User;
          setArtist(user);
        });
    song &&
      song.license_address &&
      getLicenseOwners(song) &&
      getLicenseLegalContractURI(song.license_address);
    song && song.type === SongType.MASTER_COPY && getLegalContractURI();
  }, [song]);

  const togglePlayPause = () => {
    const prevValue = isPlaying;
    setIsPlaying(!prevValue);
    if (!prevValue) {
      (audioPlayer.current as HTMLAudioElement).play();
      animationRef.current = requestAnimationFrame(whilePlaying);
    } else {
      (audioPlayer.current as HTMLAudioElement).pause();
      cancelAnimationFrame(animationRef.current);
    }
  };
  const whilePlaying = () => {
    if (audioPlayer.current !== null) {
      (progressBar.current as HTMLInputElement).value =
        audioPlayer.current.currentTime.toString();
      changePlayerCurrentTime();
      animationRef.current = requestAnimationFrame(whilePlaying);
    }
  };
  const changePlayerCurrentTime = () => {
    const slider = progressBar.current as HTMLInputElement;
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);
    const value = parseInt(slider.value);
    const percentage = ((value - min) / (max - min)) * 100;
    (progressBar.current as HTMLInputElement).style.setProperty(
      "background",
      "linear-gradient(to right, #fff 0%, #fff " +
        percentage +
        "%, #C6C6C6 " +
        percentage +
        "%, #C6C6C6 100%)"
    );
    setCurrentTime(parseInt((progressBar.current as HTMLInputElement).value));
  };

  const changeRange = () => {
    (audioPlayer.current as HTMLAudioElement).currentTime = parseInt(
      (progressBar.current as HTMLInputElement).value
    );
    changePlayerCurrentTime();
  };

  const onSongClick = (song: MasterCopy) => {
    navigate(`/song/${song.id}`);
    window.scrollTo(0, 0);
  };

  const onPublishClick = () => {
    setShowListToMarketplaceModal(true);
  };

  const onLicenseClick = () => {
    navigate("/license_hub_info", { state: { song: song } });
  };

  const onManageLicensesClick = async () => {
    navigate(`/song/${song.id}/manage_licenses`);
  };

  const onSongAvailableClick = () => {
    navigate("/user/" + artist.wallet_address);
  };

  const onBuyNowClick = () => {
    if (auth.user) {
      setShowSongCheckoutModal(true);
    } else {
      navigate("/connect_wallet");
    }
  };

  const onBuyerLicenseClick = async () => {
    if (!auth.user) {
      navigate("/connect_wallet");
      return;
    }
    setShowSongLicenseModal(true);
  };

  const onCheckoutCloseClick = () => {
    setShowSongCheckoutModal(false);
  };
  const onCheckoutSuccess = () => {
    setShowSongCheckoutModal(false);
    setShowCheckoutSuccessModal(true);
    // Refresh the song after we bought it
    if (song.type === SongType.MASTER_COPY) {
      getSong();
    } else {
      // We need to refresh the page, except we go to song.type === 2
      getCollection();
    }
  };

  const onListSuccess = () => {
    setShowListToMarketplaceModal(false);
    setShowListSuccess(true);
    // Refresh the song after we published it to marketplace
    getSong();
  };

  const onListToSecondarySuccess = () => {
    setShowListToSecondaryMarketModal(false);
    setShowListSuccess(true);
    // Refresh the song after we published it to marketplace
    getCollection();
    getTokensFromCollection();
  };

  const formatUnixTimestamp = (unixTimestamp: number) => {
    let date = new Date(unixTimestamp * 1000);
    let year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : "0" + month;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : "0" + day;

    return month + "/" + day + "/" + year;
  };
  const onLicenseCloseClick = () => {
    setShowSongLicenseModal(false);
  };

  const onLicenseSuccess = () => {
    setShowSongLicenseModal(false);
    setLicensed(true);
  };

  const isExpired = (unixTimestamp: number) => {
    let date = new Date(unixTimestamp * 1000);
    let today = new Date();
    return today > date;
  };
  const onLoadedMetadata = () => {
    if (!isNaN((audioPlayer.current as HTMLAudioElement).duration)) {
      const seconds = Math.floor(
        (audioPlayer.current as HTMLAudioElement).duration
      );
      setDuration(seconds);
      (progressBar.current as HTMLInputElement).max = seconds.toString();
    }
  };

  const isSongLaunched = () => {
    if (song.type === SongType.COLLECTIBLE || song.type === SongType.TOKEN) {
      return !isFutureListing(song.launch_date.toString());
    } else if (song.type === SongType.MASTER_COPY) {
      return listing && !isFutureListing(listing.start_time);
    }
  };

  const onContractAddressClick = (address: string) => {
    const baseUrl = isPolygonMainnet
      ? "https://polygonscan.com/address/"
      : "https://mumbai.polygonscan.com/address/";
    window.location.assign(baseUrl + address);
  };

  const onMetadataClick = (song: Song) => {
    if (!song.metadata_base_url || !isSongLaunched()) {
      return;
    }
    if (song.type === SongType.MASTER_COPY) {
      window.location.assign(song.metadata_base_url + "1");
    } else if (song.type === SongType.TOKEN) {
      window.location.assign(song.metadata_base_url + song.tokenId);
    } else {
      window.location.assign(song.metadata_base_url);
    }
  };

  const onListToSecondaryMarketplaceClick = () => {
    setShowListToSecondaryMarketModal(true);
  };

  const getLicenseOwners = async (song: Song) => {
    const jsonData = {
      license_address: song.license_address,
    };
    axios
      .get(
        process.env.REACT_APP_BACKEND_URL +
          "/get_licenses_from_address?license_address=" +
          song.license_address
      )
      .then((response: AxiosResponse) => {
        const licenses = response.data.licenses as Array<License>;
        setLicenses(licenses);
      });
  };

  const getCutContractAddress = (contractAddress: string) => {
    if (window.screen.width < 650) {
      return contractAddress.substring(0, 7) + "...";
    }
    return contractAddress.substring(0, 15) + "...";
  };

  const getCutMetadata = (metadataBaseUri: string) => {
    if (window.screen.width < 650) {
      return "ipfs://" + getHash(metadataBaseUri).substring(0, 7) + "...";
    }
    return "ipfs://" + getHash(metadataBaseUri).substring(0, 15) + "...";
  };

  const getHash = (metadataBaseUri) => {
    const hash = metadataBaseUri.substring(
      metadataBaseUri.indexOf("/ipfs") + 6,
      metadataBaseUri.length
    );
    return hash;
  };

  const isFutureListing = (startTime: string) => {
    return parseInt(startTime) > new Date().getTime() / 1000;
  };

  useEffect(() => {
    listing &&
      !isCollection &&
      setListingTime(getRemainingListingTime(listing.start_time));
  }, [listing && !isCollection]);

  useEffect(() => {
    if (song && isCollection && song.launch_date !== undefined) {
      setListingTime(getRemainingListingTime(song.launch_date.toString()));
    }
  }, [song, isCollection]);

  const onSongTokenClick = (songToken: SongToken, listing: Listing) => {
    setListing(listing);
    setCheckoutWithToken(true);
    setShowSongCheckoutModal(true);
    setCheckoutSongToken(
      Song.fromSongToken(songToken, Song.toSongCollection(song))
    );
  };

  const getLegalContractURI = async () => {
    const provider = new ethers.providers.InfuraProvider(
      isPolygonMainnet ? "matic" : "maticmum",
      process.env.REACT_APP_INFURA_API_KEY
    );
    const signer = new ethers.Wallet(
      isPolygonMainnet
        ? process.env.REACT_APP_SIGNER_PRIVATE_KEY
        : process.env.REACT_APP_DEV_SIGNER_PRIVATE_KEY,
      provider
    );
    const composeNFTContract = new ethers.Contract(
      song.nft_address,
      ComposeNFTArtifact.abi,
      signer
    );
    const licenseURI = await composeNFTContract.getLicenseURI();
    setLegalContractURI(licenseURI);
  };

  const getLicenseLegalContractURI = async (license_address) => {
    const provider = new ethers.providers.InfuraProvider(
      isPolygonMainnet ? "matic" : "maticmum",
      process.env.REACT_APP_INFURA_API_KEY
    );
    const signer = new ethers.Wallet(
      isPolygonMainnet
        ? process.env.REACT_APP_SIGNER_PRIVATE_KEY
        : process.env.REACT_APP_DEV_SIGNER_PRIVATE_KEY,
      provider
    );
    const licenseSoulboundNFTContract = new ethers.Contract(
      license_address,
      LicenseSoulboundNFTArtifact.abi,
      signer
    );
    const licenseURI = await licenseSoulboundNFTContract.getLicenseURI();
    setLicenseLegalContractURI(licenseURI);
  };

  const onViewLegalContractClick = async () => {
    if (legalContractURI == "") {
      return;
    }
    window.location.assign(
      "https://arweave.net/" +
        legalContractURI.substring(
          legalContractURI.indexOf("//") + 2,
          legalContractURI.length
        )
    );
  };

  const onViewLicenseLegalContractClick = async () => {
    if (licenseLegalContractURI == "") {
      return;
    }
    window.location.assign(
      "https://compose-nft.infura-ipfs.io/ipfs/" +
        licenseLegalContractURI.substring(
          licenseLegalContractURI.indexOf("//") + 2,
          licenseLegalContractURI.length
        )
    );
  };

  const onShareClick = () => {
    setShowShareSheet(!showShareSheet);
  };

  const onTwitterShareClick = async () => {
    const currentURL = window.location.href;
    window.open(
      `https://twitter.com/intent/tweet?text=Dropping%20my%20single%20on%20ComposeNFT&via=composenft_&url=${currentURL}`
    );
    setShowShareSheet(false);
    setNotificationMessage("Successfully shared to Twitter");
    setShowCopySuccess(true);
    await sleep(2000);
    setShowCopySuccess(false);
  };

  const onCopyClick = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setShowShareSheet(false);
    setNotificationMessage("Successfully copied URL");
    setShowCopySuccess(true);
    await sleep(2000);
    setShowCopySuccess(false);
  };

  return (
    <div style={{ background: "#000d30" }}>
      <NavBar theme={theme} themeToggler={toggleTheme} />
      {song && song.song_name !== "" && (
        <SongDetailPageContainer>
          <ModalOutlet />
          <AlbumCoverColumn>
            <Image src={song.song_cover_s3_link} />
            <PlayOverlay>
              <Row align={"flex-start"} style={{ width: "100%" }}>
                <Column>
                  <SongText>{song.song_name}</SongText>
                  {artist && <ArtistText>{artist.stage_name}</ArtistText>}
                </Column>

                {isSongLaunched() && (
                  <PlayButton
                    bg={isPlaying ? pause_icon : play_icon}
                    onClick={togglePlayPause}
                  />
                )}
              </Row>
              {isSongLaunched() ? (
                <>
                  <audio
                    ref={audioPlayer}
                    src={song.preview_song_url}
                    preload="metadata"
                    onLoadedMetadata={onLoadedMetadata}
                  />
                  <SeekBarRow>
                    <SeekBar
                      type="range"
                      max="100"
                      min="0"
                      defaultValue={0}
                      ref={progressBar}
                      onChange={changeRange}
                    />
                  </SeekBarRow>
                  <DurationRow>
                    <Text>{calculateTime(currentTime)}</Text>
                    <Text>{calculateTime(duration)}</Text>
                  </DurationRow>
                </>
              ) : (
                <Text style={{ margin: "5px", marginBottom: "15px" }}>
                  Audio will be available on the launch date.
                </Text>
              )}
              <Row />
            </PlayOverlay>
          </AlbumCoverColumn>
          <RightColumn>
            {artist && (
              <Column style={{ paddingTop: "5%", marginRight: "10%" }}>
                <Row
                  style={{
                    justifyContent: "space-between",
                    paddingBottom: "2%",
                  }}
                >
                  <ClickableContainer
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    <img src={arrow_left} />
                    <ArtistText>Back</ArtistText>
                  </ClickableContainer>

                  <div style={{ position: "relative" }}>
                    <ClickableContainer onClick={onShareClick}>
                      <img src={share} />
                      <ArtistText>Share</ArtistText>
                    </ClickableContainer>
                    {showShareSheet && (
                      <ShareSheet>
                        <ClickableContainer
                          style={{ gap: "10px" }}
                          onClick={onTwitterShareClick}
                        >
                          <img src={twitter_logo} />
                          <Text>Twitter</Text>
                        </ClickableContainer>
                        <ClickableContainer
                          style={{ gap: "10px" }}
                          onClick={onCopyClick}
                        >
                          <img src={copy_logo} />
                          <Text>Copy</Text>
                        </ClickableContainer>
                      </ShareSheet>
                    )}
                  </div>
                </Row>
                {showCopySuccess && (
                  <NotificationMessage>
                    <Text style={{ color: "black" }}>
                      {notificationMessage}
                    </Text>
                  </NotificationMessage>
                )}

                <ArtistProfileSection>
                  <div>
                    <RoundedProfilePic bg={artist.profile_pic_s3_link} />
                    <Column style={{ marginTop: "20px", paddingLeft: "20px" }}>
                      <ArtistText>{artist.stage_name}</ArtistText>
                      {songsByArtist &&
                        songsByArtist.filter((song) => {
                          return (
                            song.for_sale || song.type === SongType.COLLECTIBLE
                          );
                        }) && (
                          <SongAvailableText onClick={onSongAvailableClick}>
                            {songsByArtist.filter((song) => {
                              return (
                                song.for_sale ||
                                song.type === SongType.COLLECTIBLE
                              );
                            }).length > 1
                              ? songsByArtist.filter((song) => {
                                  return (
                                    song.for_sale ||
                                    song.type === SongType.COLLECTIBLE
                                  );
                                }).length + " Songs Available"
                              : "1 Song Available"}
                          </SongAvailableText>
                        )}
                    </Column>
                  </div>
                  {song.type === SongType.COLLECTIBLE &&
                    (!song.for_secondary_sale ? (
                      <Column
                        style={{
                          justifyContent: "center",
                          alignItems: "center",
                          width: "10%",
                        }}
                      >
                        <CircularProgressBarWrapper
                          value={
                            isOwnerSong
                              ? (song.minted / song.quantity) * 100
                              : ((song.quantity - song.minted) /
                                  song.quantity) *
                                100
                          }
                          text={
                            isOwnerSong
                              ? `${song.minted}/${song.quantity}`
                              : `${song.quantity - song.minted}/${
                                  song.quantity
                                }`
                          }
                          styles={{
                            // Customize the root svg element
                            root: {},
                            // Customize the path, i.e. the "completed progress"
                            path: {
                              // Path color
                              stroke: "#9D7CFF",
                            },
                            text: {
                              // Text color
                              fill: "#9D7CFF",
                            },
                          }}
                        />
                        {isOwnerSong ? (
                          song.quantity - song.minted > 0 ? (
                            <Text>{song.minted} Sold</Text>
                          ) : (
                            <Text>Sold out</Text>
                          )
                        ) : song.quantity - song.minted > 0 ? (
                          <Text style={{ whiteSpace: "nowrap" }}>
                            {song.quantity - song.minted} Remaining
                          </Text>
                        ) : (
                          <Text>Sold out</Text>
                        )}
                      </Column>
                    ) : (
                      <RoyaltyPercentage song={song} />
                    ))}
                  {auth.user &&
                    song &&
                    song.type === SongType.TOKEN &&
                    song.owner_address === auth.user.wallet_address &&
                    song.for_sale && (
                      <>
                        <TokenOwnerInfoContainer>
                          <Text style={{ margin: 0 }}>
                            Token #{song.tokenId}
                          </Text>
                          <Text style={{ margin: 0, color: "#ffffff50" }}>
                            Owned
                          </Text>
                          <Row gap={"10px"} style={{ marginTop: "10px" }}>
                            <Text>Listed Price</Text>
                            <GradientText>{song.price} ETH</GradientText>
                          </Row>
                        </TokenOwnerInfoContainer>
                      </>
                    )}
                </ArtistProfileSection>
              </Column>
            )}
            {song && <DescriptionText>{song.description}</DescriptionText>}

            {song && song.type === SongType.MASTER_COPY && (
              <Text style={{ marginTop: "5%" }} size={"14px"}>
                <b>Current Owner:</b> {song.owner_address}
              </Text>
            )}

            {(song.youtube_url || song.spotify_url) && (
              <Row align={"flex-start"} style={{ alignItems: "center" }}>
                <b>Listen to Full Song on:</b>
                {song.spotify_url !== null && song.spotify_url !== "" && (
                  <ClickableRow
                    onClick={() => {
                      window.location.assign(song.spotify_url);
                    }}
                  >
                    <img src={spotify_logo} />
                    Spotify
                  </ClickableRow>
                )}
                {song.youtube_url !== null && song.youtube_url !== "" && (
                  <ClickableRow
                    onClick={() => {
                      window.location.assign(song.youtube_url);
                    }}
                  >
                    <img src={youtube_logo} />
                    Youtube
                  </ClickableRow>
                )}
              </Row>
            )}

            {isOwnerSong ? (
              song.type === SongType.COLLECTIBLE &&
              (!song.for_secondary_sale ? (
                <CollectiblePriceContainer
                  song={song}
                  onListToSecondaryMarketplaceClick={
                    onListToSecondaryMarketplaceClick
                  }
                  collectorsRef={collectorsRef}
                />
              ) : (
                songTokens.length > 0 && (
                  <SecondarySaleBox
                    song={song}
                    songTokensForSale={songTokens
                      .filter((song) => {
                        return song.for_sale;
                      })
                      .sort((songTokenA, songTokenB) => {
                        return songTokenA.price < songTokenB.price ? -1 : 1;
                      })}
                    currentTokenSort={currentTokenSort}
                    setCurrentTokenSort={setCurrentTokenSort}
                    songTokenClick={onSongTokenClick}
                  />
                )
              ))
            ) : (
              <>
                {song.price === null && song.type === SongType.TOKEN && (
                  <Row
                    gap="10px"
                    align="flex-start"
                    style={{ marginTop: "1rem", flexWrap: "wrap" }}
                  >
                    <BuyersPriceContainer>
                      <Row
                        style={{ justifyContent: "flex-start", gap: "10px" }}
                      >
                        <img
                          style={{ width: "30px" }}
                          src={collectible_nft_icon}
                        />
                        <PriceText>Collectible NFT</PriceText>
                      </Row>
                      <Text style={{ margin: 0 }}>Token #{song.tokenId}</Text>

                      <Row
                        gap={"1rem"}
                        style={{ justifyContent: "flex-start" }}
                      >
                        <ExtraInfoContainer>
                          <GradientText>Owned</GradientText>
                          <GeneralButton
                            disabled={!song.for_secondary_sale}
                            onClick={() => {
                              setShowListTokenToMarketplace(true);
                            }}
                          >
                            Sell
                          </GeneralButton>
                        </ExtraInfoContainer>
                      </Row>
                      {!song.for_secondary_sale && (
                        <Text size={"12px"}>
                          Reselling will be available once the drop ends
                        </Text>
                      )}
                    </BuyersPriceContainer>
                  </Row>
                )}

                {song.price !== null &&
                  song.type === SongType.TOKEN &&
                  songTokens.length > 0 && (
                    <SecondarySaleBox
                      songTokenClick={onSongTokenClick}
                      song={song}
                      currentTokenSort={currentTokenSort}
                      setCurrentTokenSort={setCurrentTokenSort}
                      songTokensForSale={songTokens
                        .filter((song) => {
                          return song.for_sale;
                        })
                        .sort((songTokenA, songTokenB) => {
                          return songTokenA.price < songTokenB.price ? -1 : 1;
                        })}
                    />
                  )}

                {song.price > 0 &&
                  (song.type === SongType.MASTER_COPY ||
                    (song.type === SongType.COLLECTIBLE &&
                      !song.for_secondary_sale)) && (
                    <Row
                      gap="10px"
                      align="flex-start"
                      style={{
                        marginTop: "auto",
                        flexWrap: "wrap",
                        gap: "50px",
                      }}
                    >
                      <BuyersPriceContainer>
                        <Row
                          style={{ justifyContent: "flex-start", gap: "10px" }}
                        >
                          <img
                            style={{ width: "30px" }}
                            src={
                              song.type === SongType.MASTER_COPY
                                ? master_copy_icon
                                : collectible_nft_icon
                            }
                          />
                          {song.type === SongType.MASTER_COPY ? (
                            <PriceText>Master Copy</PriceText>
                          ) : (
                            <PriceText>Collectible NFT</PriceText>
                          )}
                        </Row>

                        <Row
                          gap={"1rem"}
                          style={{ justifyContent: "flex-start" }}
                        >
                          {((listing && !isFutureListing(listing.start_time)) ||
                            (song.type === SongType.COLLECTIBLE &&
                              song.minted < song.quantity)) && (
                            <ExtraInfoContainer>
                              <GradientText>{song.price} ETH</GradientText>
                              <GeneralButton
                                onClick={onBuyNowClick}
                                disabled={isFutureListing(
                                  song.type === SongType.COLLECTIBLE
                                    ? song.launch_date.toString()
                                    : listing.start_time
                                )}
                              >
                                Buy Now
                              </GeneralButton>
                              <BuyItOnDesktop>Buy it on Desktop</BuyItOnDesktop>
                            </ExtraInfoContainer>
                          )}
                        </Row>
                      </BuyersPriceContainer>

                      {!licensed &&
                        song.license_address !== null &&
                        song.license_address !== "" && (
                          <LicensePriceContainer>
                            <Row
                              style={{
                                justifyContent: "flex-start",
                                gap: "10px",
                              }}
                            >
                              <img
                                style={{ width: "30px" }}
                                src={license_icon}
                              />
                              <PriceText>License</PriceText>
                            </Row>
                            <LicenseButton
                              secondary
                              onClick={onBuyerLicenseClick}
                            >
                              Rent
                            </LicenseButton>
                            <LicenseBuyItOnDesktopRow align={"space-evenly"}>
                              <BuyItOnDesktop style={{ marginLeft: "20px" }}>
                                Available
                              </BuyItOnDesktop>
                              <BuyItOnDesktop style={{ marginLeft: "20px" }}>
                                Rent on Desktop
                              </BuyItOnDesktop>
                            </LicenseBuyItOnDesktopRow>
                          </LicensePriceContainer>
                        )}

                      {license && (
                        <LicenseContainer>
                          <Row gap={"10px"}>
                            <img style={{ width: "30px" }} src={license_icon} />
                            <Text bold={true} size={"22px"}>
                              Licensed
                            </Text>
                          </Row>
                          <Text>
                            License Status:{" "}
                            {license.revoked
                              ? "Revoked"
                              : isExpired(parseInt(license.expiration))
                              ? "Expired"
                              : "Active"}
                            <br />
                            <br />
                            License Type: Youtube
                            <br />
                            <br />
                            Expiration Date:{" "}
                            {formatUnixTimestamp(parseInt(license.expiration))}
                          </Text>
                        </LicenseContainer>
                      )}
                    </Row>
                  )}

                {song.type === SongType.COLLECTIBLE &&
                  song.for_secondary_sale &&
                  songTokens.length > 0 && (
                    <SecondarySaleBox
                      song={song}
                      songTokensForSale={songTokens
                        .filter((song) => {
                          return song.for_sale;
                        })
                        .sort((songTokenA, songTokenB) => {
                          return songTokenA.price < songTokenB.price ? -1 : 1;
                        })}
                      currentTokenSort={currentTokenSort}
                      setCurrentTokenSort={setCurrentTokenSort}
                      songTokenClick={onSongTokenClick}
                    />
                  )}
              </>
            )}

            {(listing && isFutureListing(listing.start_time)) ||
            (song &&
              song.type === SongType.COLLECTIBLE &&
              isFutureListing(song.launch_date.toString())) ? (
              <LaunchCountdown>
                <LaunchCountdownText>Launch Countdown</LaunchCountdownText>
                <Row align={"flex-start"} style={{ flexWrap: "wrap" }}>
                  {days.map((val, index) => (
                    <NumberContainer key={val + "days" + index}>
                      <NumberInput
                        maxLength={1}
                        onChange={() => {}}
                        value={val}
                        disabled={true}
                        type="text"
                      />
                    </NumberContainer>
                  ))}
                  <LaunchCountdownTimeText style={{ marginRight: "10px" }}>
                    days
                  </LaunchCountdownTimeText>
                  {hours.map((val, index) => (
                    <NumberContainer key={val + "hrs" + index}>
                      <NumberInput
                        maxLength={1}
                        onChange={() => {}}
                        value={val}
                        disabled={true}
                        type="text"
                      />
                    </NumberContainer>
                  ))}
                  <LaunchCountdownTimeText style={{ marginRight: "10px" }}>
                    hours
                  </LaunchCountdownTimeText>
                  {minutes.map((val, index) => (
                    <NumberContainer key={val + "mnts" + index}>
                      <NumberInput
                        maxLength={1}
                        onChange={() => {}}
                        value={val}
                        disabled={true}
                        type="text"
                      />
                    </NumberContainer>
                  ))}
                  <LaunchCountdownTimeText>minutes</LaunchCountdownTimeText>
                </Row>
                {listingTime && (
                  <LaunchTimeText>
                    {" "}
                    {listingTime["days"]} days {listingTime["hrs"]} hours{" "}
                    {listingTime["mnts"]} minutes
                  </LaunchTimeText>
                )}
              </LaunchCountdown>
            ) : (
              <>
                {isOwnerSong && song.type === SongType.MASTER_COPY && (
                  <ButtonsRow>
                    {!song.for_sale && (
                      <GeneralButton onClick={onPublishClick}>
                        Publish
                      </GeneralButton>
                    )}
                    {(song && song.license_address === null) ||
                    song.license_address === undefined ? (
                      <GeneralButton
                        secondary={!song.for_sale}
                        onClick={onLicenseClick}
                      >
                        Create License
                      </GeneralButton>
                    ) : (
                      <ManageLicensesButton
                        secondary={!song.for_sale}
                        onClick={onManageLicensesClick}
                      >
                        Manage Licenses
                      </ManageLicensesButton>
                    )}
                  </ButtonsRow>
                )}
              </>
            )}
          </RightColumn>
        </SongDetailPageContainer>
      )}

      {showSongCheckoutModal && (
        <SongCheckoutModal
          song={checkoutWithToken ? checkoutSongToken : song}
          artist={artist}
          onCheckoutCloseClick={onCheckoutCloseClick}
          onCheckoutSuccess={onCheckoutSuccess}
          listing={listing}
        />
      )}
      {showListToMarketplaceModal && (
        <ListToMarketplaceModal
          song={song}
          theme={theme}
          showListToMarketplaceModal={showListToMarketplaceModal}
          onListToMarketplaceCloseClick={() => {
            setShowListToMarketplaceModal(false);
          }}
          onListSuccess={onListSuccess}
        />
      )}

      {showListToSecondaryMarketModal && (
        <ListToSecondaryMarketModal
          song={song}
          theme={theme}
          showListToMarketplaceModal={showListToSecondaryMarketModal}
          onListToMarketplaceCloseClick={() => {
            setShowListToSecondaryMarketModal(false);
          }}
          onListSuccess={onListToSecondarySuccess}
        />
      )}

      {showListTokenToMarketplace && (
        <ListTokenToMarketplaceModal
          song={song}
          onListToMarketplaceCloseClick={() => {
            setShowListTokenToMarketplace(false);
          }}
          onListSuccess={() => {
            setShowListTokenToMarketplace(false);
            setShowListSuccess(true);
            // Refresh the song after we published it to marketplace
            getCollection();
          }}
        />
      )}

      {showCheckoutSuccess && (
        <CheckoutSuccessModal
          theme={theme}
          show={showCheckoutSuccess}
          showModalToggle={setShowCheckoutSuccessModal}
          successMessage={PURCHASE_SUCCESS_MSG}
        />
      )}

      {showListSuccess && (
        <CheckoutSuccessModal
          theme={theme}
          show={showListSuccess}
          showModalToggle={setShowListSuccess}
          successMessage={LIST_TO_MARKETPLACE_SUCCESS_MSG}
        />
      )}
      {showSongLicenseModal && (
        <SongLicenseModal
          song={song}
          theme={theme}
          artist={artist}
          showSongLicenseModal={showSongLicenseModal}
          onLicenseCloseClick={onLicenseCloseClick}
          onLicenseSuccess={onLicenseSuccess}
          showResult={false}
        />
      )}
      {song && (
        <InfoSection>
          <DetailsSection>
            <SectionTitle style={{ fontSize: "24px", fontWeight: "600" }}>
              {song.type === SongType.MASTER_COPY
                ? "Master Copy Details"
                : "Collectible NFT Details"}
            </SectionTitle>
            {(song.type === SongType.MASTER_COPY ||
              song.type === SongType.TOKEN) && (
              <DetailsInfo>
                <Text>Token ID</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>
                    {song.type === SongType.MASTER_COPY ? "1" : song.tokenId}
                  </Text>
                </Row>
              </DetailsInfo>
            )}

            <DetailsInfo>
              <Text>Network</Text>
              <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                <Text>{"Layer 2 Polygon"}</Text>
              </Row>
            </DetailsInfo>
            <DetailsInfo>
              <Text>Contract Type</Text>
              <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                <Text>ERC-721</Text>
              </Row>
            </DetailsInfo>
            <DetailsInfo
              active
              onClick={() => {
                onContractAddressClick(song.nft_address);
              }}
            >
              <Text>Contract Address</Text>
              <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                <Text>{getCutContractAddress(song.nft_address)}</Text>
                <img src={arrow_right} />
              </Row>
            </DetailsInfo>

            <DetailsInfo
              active
              onClick={() => {
                onMetadataClick(song);
              }}
            >
              <Text>View Metadata</Text>
              <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                <Text>
                  {song.type === SongType.MASTER_COPY &&
                    song.metadata_base_url &&
                    (isSongLaunched()
                      ? getCutMetadata(song.metadata_base_url + "1")
                      : "Available on launch")}

                  {song.type === SongType.TOKEN &&
                    song.metadata_base_url &&
                    getCutMetadata(song.metadata_base_url + song.tokenId)}

                  {song.type === SongType.COLLECTIBLE &&
                    song.metadata_base_url &&
                    (isSongLaunched()
                      ? getCutMetadata(song.metadata_base_url)
                      : "Available on launch")}
                </Text>
                <img src={arrow_right} />
              </Row>
            </DetailsInfo>
            {(song.type === SongType.MASTER_COPY ||
              song.type === SongType.COLLECTIBLE) && (
              <DetailsInfo
                active={song.type === SongType.MASTER_COPY}
                onClick={onViewLegalContractClick}
              >
                <Text>View Legal Contract</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>
                    {song.type === SongType.MASTER_COPY
                      ? getCutContractAddress(legalContractURI)
                      : "N/A"}
                  </Text>
                  <img src={arrow_right} />
                </Row>
              </DetailsInfo>
            )}
          </DetailsSection>
          {(song.type === SongType.COLLECTIBLE ||
            song.type === SongType.TOKEN) && (
            <DetailsSection ref={collectorsRef}>
              <SectionTitle style={{ fontSize: "24px", fontWeight: "600" }}>
                Collectors
              </SectionTitle>

              <CollectorsScrollContainer
                showBottomBorder={songTokens.length > 0}
              >
                {songTokens.length > 0 ? (
                  <Column style={{ gap: "20px", marginRight: "5%" }}>
                    {songTokens.map((song, index) => {
                      return (
                        <DetailsInfo key={"details_info_collectors_" + index}>
                          <img
                            src={default_user_pic}
                            width={"30px"}
                            height={"30px"}
                            style={{ marginTop: "auto", marginBottom: "auto" }}
                          />
                          <Text style={{ marginLeft: "10px" }}>
                            {song.owner}
                          </Text>
                        </DetailsInfo>
                      );
                    })}
                  </Column>
                ) : (
                  <Column
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "14%",
                    }}
                  >
                    <img
                      src={empty_collector_icon}
                      width={"150px"}
                      height={"150px"}
                    />
                    <Text>Be the first one to collect this NFT!</Text>
                  </Column>
                )}
              </CollectorsScrollContainer>
            </DetailsSection>
          )}

          {song.license_address && (
            <DetailsSection>
              <SectionTitle style={{ fontSize: "24px", fontWeight: "600" }}>
                License Details
              </SectionTitle>
              <DetailsInfo>
                <Text>License Owners</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>{licenses ? licenses.length : 0}</Text>
                </Row>
              </DetailsInfo>

              <DetailsInfo>
                <Text>Network</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>{"Layer 2 Polygon"}</Text>
                </Row>
              </DetailsInfo>
              <DetailsInfo>
                <Text>Contract Type</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>Non-Transferrable ERC-721</Text>
                </Row>
              </DetailsInfo>
              <DetailsInfo
                active
                onClick={() => {
                  onContractAddressClick(song.license_address);
                }}
              >
                <Text>Contract Address</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>{getCutContractAddress(song.license_address)}</Text>
                  <img src={arrow_right} />
                </Row>
              </DetailsInfo>

              <DetailsInfo>
                <Text>View Metadata</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>N/A</Text>
                  <img src={arrow_right} />
                </Row>
              </DetailsInfo>
              <DetailsInfo active onClick={onViewLicenseLegalContractClick}>
                <Text>View Legal Contract</Text>
                <Row gap={"10px"} style={{ marginLeft: "auto" }}>
                  <Text>{getCutContractAddress(licenseLegalContractURI)}</Text>
                  <img src={arrow_right} />
                </Row>
              </DetailsInfo>
            </DetailsSection>
          )}
        </InfoSection>
      )}

      {songsByArtist &&
        songsByArtist.filter((artistSong) => {
          return song.id !== artistSong.id && artistSong.for_sale;
        }).length > 0 && (
          <YouMayAlsoLikeSection>
            <SectionTitle style={{ fontSize: "24px", fontWeight: "600" }}>
              You may also like
            </SectionTitle>
            <Row
              style={{
                justifyContent: "flex-start",
                marginTop: "3%",
                flexWrap: "wrap",
              }}
              gap={"2rem"}
            >
              {songsByArtist.map(
                (artist_song, index) =>
                  index < 3 &&
                  song.id !== artist_song.id &&
                  artist_song.for_sale && (
                    <MasterCopyCard
                      key={artist_song.id}
                      song={artist_song}
                      onSongClick={onSongClick}
                    />
                  )
              )}
            </Row>
          </YouMayAlsoLikeSection>
        )}
      <HowItWorksSection>
        <HowItWorksRow>
          <SectionTitle
            style={{ fontSize: "24px", fontWeight: "600", marginBottom: "3%" }}
          >
            How It Works
          </SectionTitle>
          <HowItWorks />
        </HowItWorksRow>
      </HowItWorksSection>
      <Footer />
    </div>
  );
};

export default SongDetailPage;
